import { LegacyRef, forwardRef, useState } from "react";
import PhoneInput from 'react-phone-number-input';
import { quoteContactMethods } from "../apiModels/quoteContactMethod";
import 'react-phone-number-input/style.css'
import { QuoteRequest } from "../apiModels/quoteRequest";
import { ToastContainer, toast } from 'react-toastify';
import { logError } from "../utilities";
import { submitQuoteRequest } from "../api/quoteApi";
import ContainerPadded from "./ContainerPadded";

const forwardedPhoneInput = forwardRef((props, ref: LegacyRef<HTMLInputElement>) => <input {...props} key="phone-number-field" ref={ref} className="form-control" onBlur={() => { }} onFocus={() => { }} />)

export default function RequestQuote() {
    const notifyUserOfError = (err: string) => toast(err, { type: 'error' });
    const notifyUserOfSuccess = (msg: string) => toast(msg, { type: 'success' });

    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
    const [companyName, setCompanyName] = useState<string>('');
    const [preferredContactMethod, setPreferredContactMethod] = useState<number>(quoteContactMethods.email);
    const [shipmentDetails, setShipmentDetails] = useState<string>('');
    const [submittingForm, setSubmittingForm] = useState<boolean>(false);

    const resetForm = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhoneNumber(undefined);
        setCompanyName('');
        setPreferredContactMethod(quoteContactMethods.email);
        setShipmentDetails('');
    };

    const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmittingForm(true);

        try {
            if (firstName === undefined || firstName === null || firstName === '') {
                notifyUserOfError('First name is required.');
            }

            else if (lastName === undefined || lastName === null || lastName === '') {
                notifyUserOfError('Last name is required.');
            }

            else if (email === undefined || email === null || email === '') {
                notifyUserOfError('Email is required.');
            }

            else if (shipmentDetails === undefined || shipmentDetails === null || shipmentDetails === '') {
                notifyUserOfError('Shipment details are required.');
            }

            else {

                const quoteRequest: QuoteRequest = {
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    phone: phoneNumber,
                    companyName: companyName,
                    contactMethod: preferredContactMethod,
                    shipmentDetails: shipmentDetails
                };

                await submitQuoteRequest(quoteRequest);

                notifyUserOfSuccess('Quote request submitted successfully. We will contact you soon.');
                setSubmittingForm(false);
                resetForm();
            }
        }
        catch (error: any) {
            if (error instanceof Error) {
                notifyUserOfError(error.message);
            }

            logError(error);

            setSubmittingForm(false);
        }
        finally {
            // It is important to always return false to prevent the form from submitting.
            return false;
        }
    };

    return (
        <ContainerPadded>
            <form onSubmit={submitForm}>
                <div className='row'>
                    <div className='col-4'>
                        <h1>Request a Quote</h1>
                        <p>Use this tool to request a quote for your shipment.</p>
                    </div>
                    <div className='col-8'>
                        <div className='row'>
                            <div className='col-6'>
                                <div className="mb-3">
                                    <label htmlFor="first-name" className="form-label">First Name</label>
                                    <input className="form-control" name="first-name" placeholder="John" required={true} maxLength={500} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                                </div>
                            </div>
                            <div className='col-6'>
                                <div className="mb-3">
                                    <label htmlFor="last-name" className="form-label">Last Name</label>
                                    <input className="form-control" name="last-name" placeholder="Doe" required={true} maxLength={500} value={lastName} onChange={(e) => setLastName(e.target.value)} />
                                </div>
                            </div>
                            <div className='col-6'>
                                <div className="mb-3">
                                    <label htmlFor="email" className="form-label">Email</label>
                                    <input className="form-control" name="email" type="email" placeholder="jdoe@mail.com" required={true} maxLength={500} value={email} onChange={(e) => setEmail(e.target.value)} />
                                </div>
                            </div>
                            <div className='col-6'>
                                <div className="mb-3">
                                    <label htmlFor="phone-number" className="form-label">Phone Number</label>
                                    <PhoneInput
                                        placeholder="Enter phone number"
                                        name="phone-number"
                                        value={phoneNumber}
                                        onChange={setPhoneNumber}
                                        defaultCountry="US"
                                        inputComponent={forwardedPhoneInput} />
                                </div>
                            </div>
                            <div className='col-6'>
                                <div className="mb-3">
                                    <label htmlFor="company-name" className="form-label">Company Name</label>
                                    <input className="form-control" name="company-name" placeholder="Your Company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                                </div>
                            </div>
                            <div className='col-6'>
                                <div className="mb-3">
                                    <label htmlFor="preferred-contact-method" className="form-label">Preferred Contact Method</label>
                                    <select className="form-control" name="preferred-contact-method" value={preferredContactMethod} onChange={(e) => setPreferredContactMethod(parseInt(e.target.value))}>
                                        <option value={quoteContactMethods.email}>Email</option>
                                        <option value={quoteContactMethods.phone}>Phone</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label htmlFor="shipment-details" className="form-label">Shipment Details</label>
                            <textarea className="form-control"
                                name="shipment-details"
                                rows={3}
                                value={shipmentDetails}
                                required={true}
                                maxLength={5000}
                                placeholder="Please provide details about your shipment."
                                onChange={(e) => setShipmentDetails(e.target.value)}></textarea>
                        </div>

                        <button className="btn btn-primary" disabled={submittingForm}>{submittingForm ? <span>Submitting</span> : <span>Submit</span>}</button>
                    </div>
                </div>
                <ToastContainer />
            </form>
        </ContainerPadded>
    );
}
